import {
  generateSectionItems,
  stripHash,
} from '@wix/blocks-widget-services/editor';
import type {
  WidgetAPI,
  WidgetAPIEvent,
  WidgetDescriptor,
} from '@wix/document-services-types';
import type {
  AppManifest,
  WidgetManifest as WidgetStageData,
} from '@wix/editor-platform-sdk-types';
import _ from 'lodash';
import type { IFedopsLogger } from '../services/FedopsLogger/FedopsLogger';
import {
  getEditorPresenceByWidgetId,
  getInstallationSettingsByWidgetId,
} from './installationSettingsUtils';
import type { ITranslations } from '../services/Translations/ITranslations';
import type { ExtendedBlocksAppData } from '../types/ExtendedBlocksAppData';
import { getUniqueControllerType } from '../services/StructureBuilder/structureUtil';
import { getWidgetPresetsManifest } from './widgetPresetsUtils';
import type {
  EditorPresence,
  InstallationSettings,
} from '@wix/ambassador-devcenter-v1-component/types';
import type { SectionItem } from '@wix/blocks-widget-services';
import type { IBobWidgetManifestFetcher } from '../services/BobWidgetServices/IBobWidgetManifestFetcher';
import type { IBobWidgetImages } from '../services/BobWidgetServices/IBobWidgetImages';

type WidgetEntries = [string, WidgetDescriptor][];

type WidgetPresenceData = {
  widgetId: string;
  widgetDescriptor: WidgetDescriptor;
  presetsToShowInAddPanel?: string[];
};

export interface WidgetManifest {
  name: string;
  propertiesSchema:
    | WidgetDescriptor['widgetApi']['propertiesSchemas']
    | undefined;
}

export interface ExtendedAppManifest extends AppManifest {
  widgets?: Record<string, WidgetManifest>;
  customDefinitions?: unknown[];
  addPanelSectionDefinition?: AddPanelSectionDefinition;
}

const DEFAULT_ACTIONS = {
  helpId: 'b22b6385-70e3-45d8-81e0-3279360c256b',
};

const getIconButtons = () => {
  const iconButtonsOverride = {
    ...DEFAULT_ACTIONS,
    iconButtons: {
      add: {
        actionId: 'addElements',
        behavior: 'CUSTOM_ACTION',
      },
      connect: {
        behavior: 'DEFAULT',
      },
      upgrade: {
        behavior: 'DEFAULT' as const,
      },
    },
  } as const;
  return iconButtonsOverride;
};
const createControllerStageData = (
  { name: displayName, presets, widgetApi }: WidgetDescriptor,
  appData: ExtendedBlocksAppData,
  widgetId: string,
  translations: ITranslations,
  bobWidgetImages: IBobWidgetImages,
  stageDataFromServer: {},
) => {
  const editorPresenceById = getEditorPresenceByWidgetId(appData);

  const widgetPresets = getWidgetPresetsManifest(
    appData,
    widgetId,
    presets!,
    bobWidgetImages,
    editorPresenceById[widgetId]?.presetsEditorPresence,
  );

  // this part of stage data has higher priority than stageDataFromServer
  const nonOverridableStageData = {
    default: {
      gfpp: {
        desktop: {
          iconButtons: {
            ...(_.isEmpty(widgetApi.propertiesSchemas)
              ? {
                  connect: {
                    behavior: 'HIDE',
                  },
                }
              : {}),
          },
        },
      },
    },
  };

  const defaultStageData = {
    default: {
      connections: {
        '*': {
          behavior: {
            duplicatable: false,
            canReparent: false,
          },
        },
      },
      behavior: {
        rotatable: false,
        closable: false,
      },
      displayName,
      nickname: _.camelCase(displayName),
      gfpp: {
        desktop: {
          ...getIconButtons(),
          ...widgetPresets,
        },
        mobile: {
          ...DEFAULT_ACTIONS,
          ...widgetPresets,
          iconButtons: {},
        },
      },
    },
  } satisfies WidgetStageData;

  return _.merge(
    {},
    defaultStageData,
    stageDataFromServer,
    nonOverridableStageData,
  );
};
type ControllerStageData = ReturnType<typeof createControllerStageData>;

const MEMBER_KINDS = {
  MEMBER: 'member',
  FUNCTION: 'function',
} as const;

const generateWidgetManifest = (widget: WidgetDescriptor): WidgetManifest => ({
  name: widget.name,
  propertiesSchema: widget.widgetApi.propertiesSchemas,
});

const pascalCase = (str: string) => _.upperFirst(_.camelCase(str));
const getEventName = (name: string) => `on${pascalCase(name)}`;

function generateWidgetEvents(events: WidgetAPIEvent[]) {
  return events.reduce(
    (acc, event) =>
      _.assign(acc, {
        [getEventName(event.name)]: {
          type: event.name,
          description: event.description,
        },
      }),
    {},
  );
}

function generateWidgetMembers(
  { functions, propertiesSchemas }: WidgetAPI,
  schemaDriver: any,
) {
  const { MEMBER, FUNCTION } = MEMBER_KINDS;

  const members = _.assign(
    {},
    _(propertiesSchemas)
      .mapKeys((prop) => _.head(_.keys(prop.structure)))
      .mapValues((prop) => {
        schemaDriver.set.schema(prop.structure);
        return { description: schemaDriver.get.description(), kind: MEMBER };
      })
      .value(),
    _(functions)
      .mapKeys('name')
      .mapValues(({ description, params }) => ({
        description,
        params,
        kind: FUNCTION,
      }))
      .value(),
  );

  return members;
}

function generateWidgetExports(widgetApi: WidgetAPI, schemaDriver: any) {
  return {
    eventHandlers: generateWidgetEvents(widgetApi.events || []),
    synthetic: false,
    inherits: {},
    members: generateWidgetMembers(widgetApi, schemaDriver),
  };
}
type WidgetExport = ReturnType<typeof generateWidgetExports>;

const filterPresets = (
  widgetDescriptor: WidgetDescriptor,
  presetIdFilter: string[],
): WidgetDescriptor => {
  return {
    ...widgetDescriptor,
    presets: widgetDescriptor.presets?.filter((p) =>
      presetIdFilter.includes(stripHash(p.presetId)),
    ),
  };
};

export const getPresetsToShowInAddPanel = ({
  firstPresetId,
  editorPresence,
  installationSettings,
}: {
  firstPresetId: string;
  editorPresence?: EditorPresence;
  installationSettings?: InstallationSettings;
}) => {
  const presetsEditorPresence = editorPresence?.presetsEditorPresence;
  if (presetsEditorPresence && presetsEditorPresence.length > 0) {
    return presetsEditorPresence
      .filter(({ showInAddPanel, presetId }) => showInAddPanel && presetId)
      .map(({ presetId }) => presetId!);
  } else if (installationSettings) {
    const maybeDesktopPresetId =
      installationSettings.mainPresets?.desktopPresetId;
    return maybeDesktopPresetId
      ? [maybeDesktopPresetId]
      : firstPresetId
      ? [firstPresetId]
      : undefined;
  }
};

export interface AddPanelSectionDefinition {
  title: string;
  header: string;
  type: string;
  items: SectionItem[];
}

export const createAddPanelSectionDefinition = ({
  appData,
  widgetsEntriesAndPresets,
}: {
  appData: ExtendedBlocksAppData;
  widgetsEntriesAndPresets: WidgetPresenceData[];
}): AddPanelSectionDefinition | undefined => {
  const { appDefinitionId, appDefinitionName, appFields } = appData;
  const settingsById = getInstallationSettingsByWidgetId(appData);
  const editorPresenceById = getEditorPresenceByWidgetId(appData);

  const items = widgetsEntriesAndPresets
    .filter(({ presetsToShowInAddPanel }) => {
      return presetsToShowInAddPanel
        ? presetsToShowInAddPanel.length > 0
        : true;
    })
    .flatMap(({ widgetId, presetsToShowInAddPanel, widgetDescriptor }) => {
      const shouldFilterPresets =
        presetsToShowInAddPanel && presetsToShowInAddPanel.length > 0;
      const mobilePresetId =
        settingsById[widgetId]?.mainPresets?.mobilePresetId ===
        settingsById[widgetId]?.mainPresets?.desktopPresetId
          ? undefined
          : settingsById[widgetId]?.mainPresets?.mobilePresetId;

      return generateSectionItems({
        appDefinitionId,
        baseUrls: appFields.platform?.baseUrls || {},
        widgetDescriptor: shouldFilterPresets
          ? filterPresets(widgetDescriptor, presetsToShowInAddPanel)
          : widgetDescriptor,
        id: widgetId,
        mobilePresetId,
        presetsEditorPresence:
          editorPresenceById[widgetId]?.presetsEditorPresence,
      });
    });

  if (!items.length) {
    return;
  }

  return {
    title: appDefinitionName,
    header: appDefinitionName,
    type: 'LIST',
    items,
  };
};

export const createControllersStageData = async ({
  appData,
  bobWidgetManifestFetcher,
  bobWidgetImages,
  widgetEntries,
  translations,
}: {
  appData: ExtendedBlocksAppData;
  fedopsLogger: IFedopsLogger;
  bobWidgetManifestFetcher: IBobWidgetManifestFetcher;
  bobWidgetImages: IBobWidgetImages;
  widgetEntries: WidgetEntries;
  translations: ITranslations;
}) => {
  const { appDefinitionId, appFields, components, versionFailure } = appData;

  if (versionFailure) {
    return {};
  }

  const { controllersStageData } =
    await bobWidgetManifestFetcher.getWidgetsStageData(
      appDefinitionId,
      appFields,
      components,
    );

  return widgetEntries.reduce<Record<string, ControllerStageData>>(
    (acc, [widgetId, widget]) => {
      const controllerType = getUniqueControllerType(widgetId, appDefinitionId);

      acc[controllerType] = createControllerStageData(
        widget,
        appData,
        widgetId,
        translations,
        bobWidgetImages,
        controllersStageData[controllerType],
      );

      return acc;
    },
    {},
  );
};

export const createExports = ({
  appData: { versionFailure, appDefinitionId },
  widgetEntries,
  schemaDriver,
}: {
  appData: ExtendedBlocksAppData;
  widgetEntries: WidgetEntries;
  schemaDriver: any;
}) => {
  if (versionFailure) {
    return {};
  }

  return widgetEntries.reduce<Record<string, WidgetExport>>(
    (acc, [widgetId, widget]) => {
      const controllerType = getUniqueControllerType(widgetId, appDefinitionId);

      acc[controllerType] = generateWidgetExports(
        widget.widgetApi,
        schemaDriver,
      );

      return acc;
    },
    {},
  );
};

export const createWidgets = (widgetEntries: WidgetEntries) =>
  widgetEntries.reduce<Record<string, WidgetManifest>>(
    (acc, [widgetId, widget]) => {
      acc[widgetId] = generateWidgetManifest(widget);
      return acc;
    },
    {},
  );
